/**
 * Per-telsys-type licence profiles for bulk licence assignment (APD-13993).
 *
 * <p>The system type decides which reporting licences bulk assign offers and which one is
 * pre-selected. The licence values are the page's own reporting access levels, and the labels come
 * from the shared reporting-level label resolver, so they read exactly as the licence popup does.
 * Call Centre Supervisor (Manager / WxC Supervisor) is never offered in bulk.</p>
 */
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { ReportingAccessLevels } from '../../../../../NonReact/constants/Permissions'
import { TelSysTypes } from '../../../../../NonReact/constants/TelephonyServerTypes'
import { isStringNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'
import { enterpriseReportingLevelLabel } from '../../../../../React/AddUser/Reporting/ReportingAccessLevels'
import { INTEGRATION_TYPE, IntegrationType } from '../../../../../React/TelSysConfiguration/Constants/Constants'
import { LicenceManagementListRow } from '../../../../../NonReact/Types/LicenceManagementPageTypes'

/** Reporting licences bulk assign can grant. */
export type BulkLicence =
    | ReportingAccessLevels.ESSENTIALS
    | ReportingAccessLevels.PROFESSIONAL
    | ReportingAccessLevels.CALL_CENTRE_AGENT

export const BulkProfileId = {
    TEAMS_BUSINESS: 'teamsBusiness',
    TEAMS_LEGACY: 'teamsLegacy',
    WEBEX_CALLING: 'wxc',
    BROADWORKS: 'broadworks'
} as const

export type BulkProfileId = (typeof BulkProfileId)[keyof typeof BulkProfileId]

export type BulkLicenceProfile = {
    readonly id: BulkProfileId
    /** Telsys type the labels are resolved for. */
    readonly telSysType: TelSysTypes
    /** Integration type the labels are resolved for (Teams only). */
    readonly integrationType: Nullable<IntegrationType>
    /** Licences offered, in display order. */
    readonly offered: readonly BulkLicence[]
    /** Licence pre-selected when the system is chosen. */
    readonly defaultLicence: BulkLicence
    /** Whether a device flagged HasAgent stands in for a missing agent number. */
    readonly agentFromDevice: boolean
}

export const BULK_LICENCE_PROFILES: Readonly<Record<BulkProfileId, BulkLicenceProfile>> = {
    [BulkProfileId.TEAMS_BUSINESS]: {
        id: BulkProfileId.TEAMS_BUSINESS,
        telSysType: TelSysTypes.MS_TEAMS,
        integrationType: INTEGRATION_TYPE.BUSINESS,
        offered: [
            ReportingAccessLevels.ESSENTIALS,
            ReportingAccessLevels.PROFESSIONAL,
            ReportingAccessLevels.CALL_CENTRE_AGENT
        ],
        defaultLicence: ReportingAccessLevels.PROFESSIONAL,
        agentFromDevice: true
    },
    [BulkProfileId.TEAMS_LEGACY]: {
        id: BulkProfileId.TEAMS_LEGACY,
        telSysType: TelSysTypes.MS_TEAMS,
        integrationType: INTEGRATION_TYPE.LEGACY_TEAMS,
        offered: [ReportingAccessLevels.ESSENTIALS, ReportingAccessLevels.CALL_CENTRE_AGENT],
        defaultLicence: ReportingAccessLevels.CALL_CENTRE_AGENT,
        agentFromDevice: true
    },
    [BulkProfileId.WEBEX_CALLING]: {
        id: BulkProfileId.WEBEX_CALLING,
        telSysType: TelSysTypes.WEBEX_CALLING,
        integrationType: null,
        offered: [ReportingAccessLevels.ESSENTIALS, ReportingAccessLevels.CALL_CENTRE_AGENT],
        defaultLicence: ReportingAccessLevels.ESSENTIALS,
        agentFromDevice: true
    },
    [BulkProfileId.BROADWORKS]: {
        id: BulkProfileId.BROADWORKS,
        telSysType: TelSysTypes.BROADSOFT_BROADWORKS,
        integrationType: null,
        offered: [
            ReportingAccessLevels.ESSENTIALS,
            ReportingAccessLevels.PROFESSIONAL,
            ReportingAccessLevels.CALL_CENTRE_AGENT
        ],
        defaultLicence: ReportingAccessLevels.ESSENTIALS,
        agentFromDevice: true
    }
}

export type ResolvedBulkProfile = {
    readonly profile: BulkLicenceProfile
    /** False when the telsys type was not recognised and the BroadWorks names are used instead. */
    readonly isKnownType: boolean
}

/**
 * Picks the licence profile for a telsys. An unrecognised type falls back to BroadWorks.
 *
 * <p>A Teams telsys is only Business when its integration type says so. One with no integration
 * type gets the Legacy profile, matching the page's labels and the create/update builders: the
 * backend treats it as non-Business and rejects a Real-Time user's device scope there (11227).</p>
 *
 * @param telSysType Telsys type as reported by the API.
 * @param integrationType Telsys integration type (Teams legacy/business), any casing.
 * @returns The profile, and whether the type was recognised.
 */
export const resolveBulkProfile = (
    telSysType: Nullable<string>,
    integrationType: Nullable<string>
): ResolvedBulkProfile => {
    const type: string = telSysType ?? EMPTY_STRING

    if (/teams/i.test(type)) {
        return {
            profile: /business/i.test(integrationType ?? EMPTY_STRING)
                ? BULK_LICENCE_PROFILES[BulkProfileId.TEAMS_BUSINESS]
                : BULK_LICENCE_PROFILES[BulkProfileId.TEAMS_LEGACY],
            isKnownType: true
        }
    }

    if (/webex/i.test(type)) return { profile: BULK_LICENCE_PROFILES[BulkProfileId.WEBEX_CALLING], isKnownType: true }

    if (/broad/i.test(type)) return { profile: BULK_LICENCE_PROFILES[BulkProfileId.BROADWORKS], isKnownType: true }

    return { profile: BULK_LICENCE_PROFILES[BulkProfileId.BROADWORKS], isKnownType: false }
}

/**
 * Display label for a licence under a profile, e.g. "Real-Time User" on Teams Business or
 * "CXA Premium User" on Teams Legacy.
 *
 * @param language Loaded language handler.
 * @param profile Active licence profile.
 * @param licence Licence to label.
 * @returns The localised label.
 */
export const bulkLicenceLabel = (
    language: CLanguageHandler,
    profile: BulkLicenceProfile,
    licence: BulkLicence
): string => enterpriseReportingLevelLabel(language, licence, profile.telSysType, profile.integrationType)

/**
 * The agent number a row would be licensed against: its own agent, or — when the profile allows it
 * and the device resolves to an agent — the device number.
 *
 * @param row Licence management row.
 * @param profile Active licence profile.
 * @returns The agent number, or an empty string when there is none.
 */
export const bulkAgentNumber = (row: LicenceManagementListRow, profile: BulkLicenceProfile): string => {
    if (isStringNotEmpty(row.AgentNumber)) return row.AgentNumber

    if (profile.agentFromDevice && row.HasAgent && isStringNotEmpty(row.DeviceNumber)) return row.DeviceNumber

    return EMPTY_STRING
}

/**
 * What a row must have for a licence to be assignable to it.
 *
 * @param licence Licence being assigned.
 * @returns 'agent' for the agent licence, 'device' for the others.
 */
export const bulkLicenceNeeds = (licence: BulkLicence): 'agent' | 'device' =>
    licence === ReportingAccessLevels.CALL_CENTRE_AGENT ? 'agent' : 'device'
